import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faArrowRight, 
  faNewspaper, 
  faSpinner,
  faClock,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import '../styles/PostinganTerbaru.css';

// Helper function untuk mendapatkan API URL
const getApiUrl = () => {
  // Development mode (React dev server on port 3000)
  if (window.location.port === '3000') {
    return 'http://localhost/stikbar_webv2/public';
  }
  // Production mode - use production API
  if (window.location.hostname === 'stikesbaramuli.ac.id' || window.location.hostname === 'www.stikesbaramuli.ac.id') {
    return 'https://stikesbaramuli.ac.id';
  }
  return '';
};

// Helper function untuk mendapatkan URL gambar
const getImageUrl = (gambar) => {
  if (!gambar) return '/images/logo.png';
  if (gambar.startsWith('http')) return gambar;
  return `${getApiUrl()}${gambar}`;
};

// Helper function untuk format tanggal
const formatTanggal = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const options = { day: 'numeric', month: 'short', year: 'numeric' };
  return date.toLocaleDateString('id-ID', options);
};

// Helper function untuk estimasi waktu baca
const estimateReadTime = (text) => {
  if (!text) return 1;
  const words = text.split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return minutes < 1 ? 1 : minutes;
};

const PostinganTerbaruSection = () => {
  const [postingan, setPostingan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    fetchPostingan();
    
    // Intersection Observer for fade-in animation
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.querySelector('.postingan-terbaru-section');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const fetchPostingan = async () => {
    try {
      setLoading(true);
      const baseUrl = getApiUrl();
      const response = await fetch(`${baseUrl}/api/postingan.php?limit=3`);
      const result = await response.json();
      
      if (result.success) {
        const activePosts = result.data
          .filter(post => post.is_active === 1 || post.is_active === '1')
          .slice(0, 3);
        setPostingan(activePosts);
      }
    } catch (err) {
      console.error('Error fetching postingan:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="section postingan-terbaru-section">
        <div className="container">
          <div className="section-header-center">
            <span className="section-badge">
              <FontAwesomeIcon icon={faNewspaper} /> Berita & Informasi
            </span>
            <h2>Postingan Terbaru</h2>
            <p>Ikuti berita dan informasi terkini seputar STIKES Baramuli</p>
          </div>
          <div className="loading-state">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            <p>Memuat postingan...</p>
          </div>
        </div>
      </section>
    );
  }

  if (postingan.length === 0) {
    return (
      <section className="section postingan-terbaru-section">
        <div className="container">
          <div className="section-header-center">
            <span className="section-badge">
              <FontAwesomeIcon icon={faNewspaper} /> Berita & Informasi
            </span>
            <h2>Postingan Terbaru</h2>
            <p>Ikuti berita dan informasi terkini seputar STIKES Baramuli</p>
          </div>
          <div className="empty-state">
            <FontAwesomeIcon icon={faNewspaper} size="3x" />
            <h3>Belum ada postingan</h3>
            <p>Nantikan informasi terbaru dari kami</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`section postingan-terbaru-section ${isVisible ? 'visible' : ''}`}>
      <div className="container">
        {/* Section Header */}
        <div className="section-header-center">
          <span className="section-badge animate-badge">
            <FontAwesomeIcon icon={faNewspaper} /> Berita & Informasi
          </span>
          <h2>Postingan Terbaru</h2>
          <p>Ikuti berita dan informasi terkini seputar STIKES Baramuli</p>
        </div>

        {/* Posts Grid - Modern Layout */}
        <div className="postingan-grid-modern">
          {/* Featured Post (First Post) */}
          {postingan[0] && (
            <Link 
              to={`/postingan/${postingan[0].id}`} 
              className="featured-post animate-item"
              style={{ animationDelay: '0.1s' }}
            >
              <div className="featured-image">
                <img 
                  src={getImageUrl(postingan[0].gambar)} 
                  alt={postingan[0].judul}
                  onError={(e) => { e.target.src = '/images/logo.png'; }}
                />
                <div className="featured-overlay">
                  <span className="post-kategori">
                    {postingan[0].kategori ? postingan[0].kategori.charAt(0).toUpperCase() + postingan[0].kategori.slice(1) : 'Umum'}
                  </span>
                </div>
              </div>
              <div className="featured-content">
                <div className="post-meta">
                  <span className="post-date">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    {formatTanggal(postingan[0].created_at || postingan[0].tanggal_mulai)}
                  </span>
                  <span className="post-read-time">
                    <FontAwesomeIcon icon={faClock} />
                    {estimateReadTime(postingan[0].konten)} menit baca
                  </span>
                </div>
                <h3>{postingan[0].judul}</h3>
                <p>{postingan[0].ringkasan || (postingan[0].konten && postingan[0].konten.substring(0, 150) + '...')}</p>
                <span className="read-more">
                  Baca Selengkapnya <FontAwesomeIcon icon={faChevronRight} />
                </span>
              </div>
            </Link>
          )}

          {/* Side Posts (2nd and 3rd Post) */}
          <div className="side-posts">
            {postingan.slice(1, 3).map((post, index) => (
              <Link 
                to={`/postingan/${post.id}`} 
                key={post.id}
                className="side-post animate-item"
                style={{ animationDelay: `${0.2 + index * 0.1}s` }}
              >
                <div className="side-post-image">
                  <img 
                    src={getImageUrl(post.gambar)} 
                    alt={post.judul}
                    onError={(e) => { e.target.src = '/images/logo.png'; }}
                  />
                  <span className="post-kategori">
                    {post.kategori ? post.kategori.charAt(0).toUpperCase() + post.kategori.slice(1) : 'Umum'}
                  </span>
                </div>
                <div className="side-post-content">
                  <div className="post-meta">
                    <span className="post-date">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      {formatTanggal(post.created_at || post.tanggal_mulai)}
                    </span>
                  </div>
                  <h4>{post.judul}</h4>
                  <p>{post.ringkasan || (post.konten && post.konten.substring(0, 80) + '...')}</p>
                </div>
                <div className="side-post-arrow">
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* View All Button */}
        <div className="view-all-container">
          <Link to="/postingan" className="btn-view-all">
            <span>Lihat Semua Postingan</span>
            <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </div>

      {/* Background Decoration */}
      <div className="section-decoration">
        <div className="decoration-circle circle-1"></div>
        <div className="decoration-circle circle-2"></div>
      </div>
    </section>
  );
};

export default PostinganTerbaruSection;
